/**
 * Shop section sub-navigation: Products (catalog) and Orders (history).
 * Mirrors CollectionsNav — reuses the same TeamNav tab styling so the
 * Shop module reads as one coherent section.
 *
 * Also surfaces a "Manage on Stripe →" quick-link on the right side of
 * the tab strip whenever the club's Stripe account is fully active —
 * the natural place to find refunds, disputes, payouts when you're
 * already inside the Shop. Settings → Payments still owns the full
 * Stripe Connect lifecycle (connect / complete setup / verify); this
 * button only appears once the dashboard is genuinely useful.
 */
import { useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  getStripeConnectStatus,
  getStripeDashboardLink,
} from '../api/client';
import './TeamNav.css';
import './ShopNav.css';

const TABS = [
  { to: '/products', label: 'Products' },
  { to: '/orders', label: 'Orders' },
];

export default function ShopNav() {
  const location = useLocation();
  const [stripeReady, setStripeReady] = useState(false);
  // A club-pays account has no Stripe login: its payments live in Settings.
  const [clubPays, setClubPays] = useState(false);
  const navigate = useNavigate();
  const [loadingDash, setLoadingDash] = useState(false);

  useEffect(() => {
    getStripeConnectStatus()
      .then(s => { setStripeReady(!!(s.connected && s.chargesEnabled)); setClubPays(!!s.clubPaysStripe); })
      .catch(() => { /* not connected yet — leave button hidden */ });
  }, []);

  const openStripeDashboard = async () => {
    if (clubPays) { navigate('/settings'); return; }
    setLoadingDash(true);
    try {
      const { url } = await getStripeDashboardLink();
      if (url) window.location.href = url;
    } catch {
      setLoadingDash(false);
    }
  };

  return (
    <nav className="tm-nav shop-nav" aria-label="Shop sections">
      <div className="tm-nav-tabs">
        {TABS.map(tab => (
          <NavLink
            key={tab.to}
            to={tab.to}
            className={`tm-nav-tab ${location.pathname === tab.to ? 'active' : ''}`}
          >
            {tab.label}
          </NavLink>
        ))}
      </div>
      {stripeReady && (
        <button
          type="button"
          className="shop-nav-stripe-link"
          onClick={openStripeDashboard}
          disabled={loadingDash}
          title="Open the Stripe dashboard to see payments, refunds, and payouts"
        >
          {loadingDash ? 'Opening…' : clubPays ? 'Payments and payouts →' : 'Manage on Stripe →'}
        </button>
      )}
    </nav>
  );
}
